import { Box, BoxProps, useToast } from '@chakra-ui/react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../../../hooks/auth';
import { listProductsService } from '../../../../../../services/Products/ListProductsService';
import { translateError } from '../../../../../../utils/errors';
import { maskDateTime } from '../../../../../../utils/formatters/handleMask';
import { IProductTableItem, ProductTable } from './components/ProductTable';

interface ILoadProductsProps {
  name?: string;
  page?: number;
}

export const ProductList = (props: BoxProps): JSX.Element => {
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const [productsList, setProductsList] = useState<IProductTableItem[]>([]);
  const [searchParam, setSearchParam] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>();

  const loadProducts = useCallback(
    async ({ name, page }: ILoadProductsProps) => {
      try {
        const { items: products, pages } = await listProductsService({
          name,
          page,
        });

        const parsedProducts = products.map((product) => ({
          ...product,
          formattedCreatedAt: maskDateTime(product.createdAt),
        }));

        setTotalPages(pages);
        setProductsList(parsedProducts);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados dos produtos, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setProductsList([]);

    loadProducts({
      name: searchParam,
      page: currentPage,
    });
  }, [currentPage, loadProducts, authenticatedVenture, searchParam]);

  const handleSearch = useCallback((value: string) => {
    setSearchParam(value);
  }, []);

  const debounceSearch = debounce(handleSearch, 1000);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleDeleteProduct = (product: IProductTableItem): void => {
    setProductsList((prevState) =>
      prevState.filter((prod) => prod.id !== product.id),
    );
  };

  return (
    <Box {...props}>
      <ProductTable
        products={productsList}
        currentPage={currentPage}
        onDelete={handleDeleteProduct}
        onSearch={debounceSearch}
        onPageChange={handlePageChange}
        totalPages={totalPages}
      />
    </Box>
  );
};
